/* istanbul ignore file */
import * as React from "react";

import marketConfig from "./config";

export interface BaseMarketConfig {
  supportLangs: Array<{
    name: string;
    label: string;
    locale: "en-GB" | "zh-HK" | "zh-CN" | "ms-MY";
  }>;
  primaryColor: string;
  footerMarket: string;
  sharpieBucket: string;
  sharpieOriginalBucket: string;
  fallbackLocale?: "en-GB" | "zh-HK" | "zh-CN" | "ms-MY";
}
export type MarketConfig = BaseMarketConfig;

export type MarketConfigProps = {
  config: MarketConfig;
};

type MarketConfigContextProviderProps = {
  marketConfig: BaseMarketConfig;
  propsConfig: { [key in string]: any };
};

const MarketConfigContext = React.createContext<MarketConfigProps>({
  config: marketConfig,
});

const MarketConfigContextProvider: React.FC<
  MarketConfigContextProviderProps
> = (props) => {
  const { marketConfig, propsConfig } = props;
  const value = React.useMemo(() => {
    return { config: { ...marketConfig, ...propsConfig } };
  }, [marketConfig, propsConfig]);

  return (
    <MarketConfigContext.Provider value={value}>
      {props.children}
    </MarketConfigContext.Provider>
  );
};

class MarketConfigProvider extends React.Component<{
  config: { [key in string]: any };
}> {
  render() {
    return (
      <MarketConfigContextProvider
        marketConfig={marketConfig}
        propsConfig={this.props.config}
      >
        {this.props.children}
      </MarketConfigContextProvider>
    );
  }
}

function withMarketConfig<P extends MarketConfigProps = MarketConfigProps>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof MarketConfigProps>> {
  return (props) => (
    <MarketConfigContext.Consumer>
      {(value) => {
        return <WrappedComponent {...(props as P)} config={value.config} />;
      }}
    </MarketConfigContext.Consumer>
  );
}

function useMarketConfig(): MarketConfig {
  return React.useContext(MarketConfigContext).config;
}

export { MarketConfigProvider, withMarketConfig, useMarketConfig };
